import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Trophy, 
  Target, 
  Flame, 
  BarChart3, 
  Users, 
  Clock,
  ChevronRight,
  Zap,
  Award,
  BookOpen
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();

  const challengeModes = [
    {
      icon: Trophy,
      title: 'Classic',
      description: '50 questions in 120 minutes - Real exam experience',
      color: 'bg-blue-500',
      time: '120 min',
      questions: '50'
    },
    {
      icon: Zap,
      title: 'Blitz',
      description: '5 quick questions in 6 minutes',
      color: 'bg-yellow-500',
      time: '6 min',
      questions: '5'
    },
    {
      icon: Target,
      title: 'Ladder',
      description: 'Progress from easy to hard - Subject focused',
      color: 'bg-green-500',
      time: 'Unlimited',
      questions: 'Varies'
    },
    {
      icon: BookOpen,
      title: 'Past',
      description: 'Practice with actual past exam papers',
      color: 'bg-purple-500',
      time: '120 min',
      questions: '50'
    }
  ];

  const features = [
    {
      icon: Trophy,
      title: 'Real-Time Leaderboard',
      description: 'Compete with students island-wide. Filter by school or subject.'
    },
    {
      icon: Flame,
      title: 'Streak Rewards',
      description: 'Build streaks, earn badges, and unlock achievements for consistent practice.'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Track weak topics, view progress graphs, and get personalized insights.'
    }
  ];

  const stats = [
    { value: '10K+', label: 'Active Students' },
    { value: '50', label: 'Daily Questions' },
    { value: '500+', label: 'Schools' },
    { value: '1 Hr', label: 'Time Limit' }
  ];

  const subjects = [
    { stream: 'Science', subjects: ['Physics', 'Chemistry', 'Biology'] }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="hero-bg absolute inset-0" />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium animate-fade-in-up">
                <Zap className="w-4 h-4" />
                <span>New: Weekly Challenge Mode</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none animate-fade-in-up stagger-1">
                Master Your A/L Exams
                <span className="block text-primary mt-2">One Question at a Time</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl animate-fade-in-up stagger-2">
                Join thousands of Sri Lankan students competing daily. Practice MCQs, track your progress, 
                and climb the leaderboards.
              </p>
              
              <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
                <Button 
                  size="lg" 
                  className="btn-hover text-lg px-8 py-6"
                  onClick={() => navigate(isAuthenticated ? '/challenge' : '/auth')}
                  data-testid="start-challenge-btn"
                >
                  Start Challenge
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="btn-hover text-lg px-8 py-6"
                  onClick={() => navigate('/leaderboard')}
                  data-testid="view-leaderboard-btn"
                >
                  View Leaderboard
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 animate-fade-in-up stagger-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-primary">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block animate-fade-in-up stagger-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 rounded-3xl blur-3xl" />
                <div className="relative bg-card border border-border rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Daily Challenge</span>
                    <span className="flex items-center gap-1 text-orange-500 font-semibold">
                      <Flame className="w-4 h-4" /> 7 Day Streak
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="h-3 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-primary rounded-full" />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Question 8/10</span>
                      <span className="text-primary font-medium">75 pts</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {['A) 42', 'B) 56', 'C) 28', 'D) 35'].map((opt, i) => (
                      <div 
                        key={i} 
                        className={`p-3 rounded-lg border text-center text-sm font-medium transition-all cursor-pointer
                          ${i === 1 ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Modes Section */}
      <section className="py-24 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Choose Your Challenge Mode
            </h2>
            <p className="text-muted-foreground">
              Different ways to practice based on your goals and available time.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {challengeModes.map((mode, index) => (
              <div 
                key={index}
                className="stat-card group animate-fade-in-up cursor-pointer hover:scale-105 transition-transform"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(isAuthenticated ? '/challenge' : '/auth')}
              >
                <div className={`w-12 h-12 rounded-xl ${mode.color} flex items-center justify-center mb-4`}>
                  <mode.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{mode.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{mode.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {mode.time}
                  </span>
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {mode.questions} Qs
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-muted-foreground">
              Built specifically for A/L students with features designed to boost your exam performance.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="stat-card group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Science Stream MCQs
            </h2>
            <p className="text-muted-foreground">
              Practice questions from Physics, Chemistry, and Biology.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {subjects.map((stream, index) => (
              <div key={index} className="subject-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">{stream.stream}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stream.subjects.map((subject, i) => (
                    <span 
                      key={i} 
                      className="px-3 py-1 rounded-full bg-secondary text-sm font-medium"
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary/5">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Award className="w-5 h-5 text-primary" />
              <span className="font-medium">Free to Join</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Ready to Ace Your A/Ls?
            </h2>
            <p className="text-muted-foreground">
              Join the community of high achievers. Start practicing now and see your scores improve.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Button 
                size="lg" 
                className="btn-hover text-lg px-8"
                onClick={() => navigate('/auth')}
                data-testid="get-started-btn"
              >
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src={theme === 'dark' 
                  ? "/assets/logo-dark.png"
                  : "/assets/logo-light.png"
                }
                alt="paperclass.lk logo" 
                className="w-8 h-8 rounded-lg object-contain"
              />
              <span className="font-bold text-lg">paperclass.lk</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 paperclass.lk - Built for Sri Lankan A/L Students.
            </p>
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-muted-foreground" />
              <Clock className="w-5 h-5 text-muted-foreground" />
              <Trophy className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
